import { useMemo, useState } from"react";
import { Calculator, Receipt, Building2, BarChart3 } from"lucide-react";

const fmtEUR = (n) =>
 isFinite(n) ? n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) :"—";

const TAUX_TVA = [
 { v: 20, lib:"20 % — taux normal" },
 { v: 10, lib:"10 % — taux intermédiaire" },
 { v: 5.5, lib:"5,5 % — taux réduit" },
 { v: 2.1, lib:"2,1 % — taux particulier" },
];

// ─── Onglet TVA ──────────────────────────────────────────────────────────
function CalcTVA() {
 const [mode, setMode] = useState("ht"); // 'ht' or 'ttc'
 const [montant, setMontant] = useState("1000");
 const [taux, setTaux] = useState(20);

 const m = parseFloat(montant.replace(",",".")) || 0;
 const t = taux / 100;
 const ht = mode ==="ht" ? m : m / (1 + t);
 const ttc = mode ==="ht" ? m * (1 + t) : m;
 const tva = ttc - ht;

 return (
 <div className="space-y-6">
 <div className="grid md:grid-cols-3 gap-4">
 <div className="md:col-span-1">
 <label className="block text-xs font-semibold text-slate-600 mb-1.5">Je connais le montant</label>
 <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg">
 {[
 ["ht","HT"],
 ["ttc","TTC"],
 ].map(([k, l]) => (
 <button
 key={k}
 data-testid={`calc-tva-mode-${k}`}
 onClick={() => setMode(k)}
 className={`py-2 rounded-md text-sm font-bold transition ${
 mode === k ?"bg-white text-[#15294F] shadow-sm" :"text-slate-500"
 }`}
 >
 {l}
 </button>
 ))}
 </div>
 </div>
 <div className="md:col-span-1">
 <label className="block text-xs font-semibold text-slate-600 mb-1.5">Montant {mode.toUpperCase()}</label>
 <div className="relative">
 <input
 data-testid="calc-tva-montant"
 type="text"
 inputMode="decimal"
 value={montant}
 onChange={(e) => setMontant(e.target.value)}
 className="w-full pr-8 px-3 py-2 rounded-lg border border-keyto font-mono text-lg font-bold text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
 </div>
 </div>
 <div className="md:col-span-1">
 <label className="block text-xs font-semibold text-slate-600 mb-1.5">Taux de TVA</label>
 <select
 data-testid="calc-tva-taux"
 value={taux}
 onChange={(e) => setTaux(parseFloat(e.target.value))}
 className="w-full px-3 py-2 rounded-lg border border-keyto bg-white text-sm font-semibold text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 >
 {TAUX_TVA.map((t) => (
 <option key={t.v} value={t.v}>
 {t.lib}
 </option>
 ))}
 </select>
 </div>
 </div>

 <div className="grid md:grid-cols-3 gap-4">
 {[
 { lib:"Montant HT", v: ht, color:"#2D6BE0", testid:"calc-tva-ht" },
 { lib:"TVA", v: tva, color:"#B85724", testid:"calc-tva-tva" },
 { lib:"Montant TTC", v: ttc, color:"#2F7A5C", testid:"calc-tva-ttc" },
 ].map((c) => (
 <div
 key={c.lib}
 data-testid={c.testid}
 className="rounded-xl border border-keyto bg-white p-4"
 style={{ borderTop: `4px solid ${c.color}` }}
 >
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{c.lib}</p>
 <p className="font-mono font-extrabold text-2xl text-[#15294F]">{fmtEUR(c.v)} €</p>
 </div>
 ))}
 </div>

 <div className="rounded-xl bg-slate-50 border border-keyto p-4">
 <p className="text-[10px] font-bold uppercase text-[#2D6BE0] mb-1.5">Côté écriture comptable</p>
 <p className="text-sm text-slate-700 leading-relaxed">
 Sur une vente : <span className="font-mono font-bold text-[#15294F]">411</span> au débit pour <strong>{fmtEUR(ttc)} €</strong> TTC,
 <span className="font-mono font-bold text-[#15294F]"> 707</span> (ou 706) au crédit pour <strong>{fmtEUR(ht)} €</strong> HT,
 <span className="font-mono font-bold text-[#15294F]"> 44571</span> au crédit pour <strong>{fmtEUR(tva)} €</strong> de TVA collectée.
 </p>
 </div>
 </div>
 );
}

// ─── Onglet Amortissement linéaire ───────────────────────────────────────
function CalcAmortissement() {
 const [valeur, setValeur] = useState("12000");
 const [duree, setDuree] = useState("5");
 const [moisMiseEnService, setMoisMiseEnService] = useState("1"); // mois de mise en service année 1

 const v = parseFloat(valeur.replace(",",".")) || 0;
 const d = parseInt(duree) || 1;
 const mois = Math.max(1, Math.min(12, parseInt(moisMiseEnService) || 1));
 const annuite = v / d;
 const tauxAnnuel = 100 / d;

 // Plan d'amortissement avec prorata 1re année
 const plan = useMemo(() => {
 const lines = [];
 let cumul = 0;
 // Année 1 : prorata sur (12 - mois + 1) mois
 const moisAn1 = 12 - mois + 1;
 const dot1 = (annuite * moisAn1) / 12;
 cumul += dot1;
 lines.push({ annee: 1, dotation: dot1, cumul, vnc: v - cumul, prorata: moisAn1 });
 // Années 2..d
 for (let i = 2; i <= d; i++) {
 const dot = annuite;
 cumul += dot;
 lines.push({ annee: i, dotation: dot, cumul, vnc: v - cumul });
 }
 // Année d+1 : reste (12 - moisAn1) mois
 if (mois > 1) {
 const dotEnd = annuite - dot1;
 cumul += dotEnd;
 lines.push({ annee: d + 1, dotation: dotEnd, cumul, vnc: v - cumul, prorata: mois - 1 });
 }
 return lines;
 }, [v, d, mois, annuite]);

 return (
 <div className="space-y-6">
 <div className="grid md:grid-cols-3 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 mb-1.5">Valeur d&apos;origine HT</label>
 <div className="relative">
 <input
 data-testid="calc-amo-valeur"
 type="text"
 inputMode="decimal"
 value={valeur}
 onChange={(e) => setValeur(e.target.value)}
 className="w-full pr-8 px-3 py-2 rounded-lg border border-keyto font-mono text-lg font-bold text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
 </div>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 mb-1.5">Durée d&apos;utilité (années)</label>
 <input
 data-testid="calc-amo-duree"
 type="number"
 min={1}
 max={40}
 value={duree}
 onChange={(e) => setDuree(e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-keyto font-mono text-lg font-bold text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 mb-1.5">Mois de mise en service (1-12)</label>
 <input
 data-testid="calc-amo-mois"
 type="number"
 min={1}
 max={12}
 value={moisMiseEnService}
 onChange={(e) => setMoisMiseEnService(e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-keyto font-mono text-lg font-bold text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 </div>
 </div>

 <div className="grid md:grid-cols-3 gap-4">
 <div className="rounded-xl border border-keyto bg-white p-4" style={{ borderTop:"4px solid #2D6BE0" }}>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Annuité (année pleine)</p>
 <p className="font-mono font-extrabold text-2xl text-[#15294F]">{fmtEUR(annuite)} €</p>
 </div>
 <div className="rounded-xl border border-keyto bg-white p-4" style={{ borderTop:"4px solid #2F7A5C" }}>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Taux annuel</p>
 <p className="font-mono font-extrabold text-2xl text-[#15294F]">{fmtEUR(tauxAnnuel)} %</p>
 </div>
 <div className="rounded-xl border border-keyto bg-white p-4" style={{ borderTop:"4px solid #B85724" }}>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Total amorti</p>
 <p className="font-mono font-extrabold text-2xl text-[#15294F]">{fmtEUR(v)} €</p>
 </div>
 </div>

 <div className="overflow-x-auto rounded-xl border border-keyto bg-white">
 <table className="w-full text-sm" data-testid="calc-amo-plan">
 <thead className="bg-slate-100 text-[#15294F]">
 <tr className="text-xs uppercase">
 <th className="px-3 py-2 text-left">Année</th>
 <th className="px-3 py-2 text-right">Dotation</th>
 <th className="px-3 py-2 text-right">Amort. cumulé</th>
 <th className="px-3 py-2 text-right">VNC en fin d&apos;année</th>
 <th className="px-3 py-2 text-left">Prorata</th>
 </tr>
 </thead>
 <tbody>
 {plan.map((l) => (
 <tr key={l.annee} className="border-t border-slate-100">
 <td className="px-3 py-2 font-bold text-[#15294F]">N{l.annee === 1 ?"" :"+" + (l.annee - 1)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmtEUR(l.dotation)} €</td>
 <td className="px-3 py-2 text-right font-mono">{fmtEUR(l.cumul)} €</td>
 <td className="px-3 py-2 text-right font-mono">{fmtEUR(l.vnc)} €</td>
 <td className="px-3 py-2 text-xs text-slate-500">
 {l.prorata ? `${l.prorata} mois` :"12 mois"}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 <div className="rounded-xl bg-slate-50 border border-keyto p-4">
 <p className="text-[10px] font-bold uppercase text-[#2D6BE0] mb-1.5">Côté écriture comptable</p>
 <p className="text-sm text-slate-700 leading-relaxed">
 Chaque année au 31/12 :{""}
 <span className="font-mono font-bold text-[#15294F]">681</span> au débit pour la dotation,{""}
 <span className="font-mono font-bold text-[#15294F]">28x</span> au crédit (miroir de l&apos;immobilisation
 21x). VNC au bilan = Valeur brute − Amortissements cumulés.
 </p>
 </div>
 </div>
 );
}

// ─── Onglet Ratios financiers ────────────────────────────────────────────
function CalcRatios() {
 const [inputs, setInputs] = useState({
 capPropres:"150000",
 detteLT:"80000",
 immo:"180000",
 stocks:"40000",
 creances:"60000",
 dettesCT:"55000",
 tresorerie:"25000",
 });

 const v = (k) => parseFloat(inputs[k].replace(",",".")) || 0;
 const capitauxPermanents = v("capPropres") + v("detteLT");
 const actifImmo = v("immo");
 const frng = capitauxPermanents - actifImmo;
 const bfr = v("stocks") + v("creances") - v("dettesCT");
 const tn = frng - bfr;
 const tnReel = v("tresorerie"); // pour comparaison

 return (
 <div className="space-y-6">
 {/* Inputs */}
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
 {[
 { k:"capPropres", lib:"Capitaux propres", cls:"1" },
 { k:"detteLT", lib:"Dettes long terme", cls:"16" },
 { k:"immo", lib:"Actif immobilisé", cls:"2" },
 { k:"stocks", lib:"Stocks", cls:"3" },
 { k:"creances", lib:"Créances clients", cls:"411" },
 { k:"dettesCT", lib:"Dettes court terme", cls:"401" },
 { k:"tresorerie", lib:"Trésorerie active", cls:"51/53" },
 ].map((f) => (
 <div key={f.k}>
 <label className="block text-[11px] font-semibold text-slate-600 mb-1">
 {f.lib} <span className="font-mono text-slate-400">({f.cls})</span>
 </label>
 <div className="relative">
 <input
 data-testid={`calc-ratio-${f.k}`}
 type="text"
 inputMode="decimal"
 value={inputs[f.k]}
 onChange={(e) => setInputs((s) => ({ ...s, [f.k]: e.target.value }))}
 className="w-full pr-7 px-2.5 py-1.5 rounded-md border border-keyto font-mono text-sm font-bold text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">€</span>
 </div>
 </div>
 ))}
 </div>

 {/* Résultats */}
 <div className="grid md:grid-cols-3 gap-4">
 <div className="rounded-xl border border-keyto bg-white p-5" data-testid="calc-ratio-frng">
 <p className="text-[10px] font-bold uppercase text-[#2D6BE0] mb-1">Fonds de roulement net global</p>
 <p className="font-mono font-extrabold text-3xl text-[#15294F]">{fmtEUR(frng)} €</p>
 <p className="text-xs text-slate-500 mt-2 leading-snug">
 Capitaux permanents − Actif immobilisé. Ressource stable disponible pour financer le cycle d&apos;exploitation.
 </p>
 </div>
 <div className="rounded-xl border border-keyto bg-white p-5" data-testid="calc-ratio-bfr">
 <p className="text-[10px] font-bold uppercase text-[#B85724] mb-1">Besoin en fonds de roulement</p>
 <p className="font-mono font-extrabold text-3xl text-[#15294F]">{fmtEUR(bfr)} €</p>
 <p className="text-xs text-slate-500 mt-2 leading-snug">
 Stocks + Créances − Dettes d&apos;exploitation. Capitaux gelés par l&apos;activité courante.
 </p>
 </div>
 <div className={`rounded-xl border-2 p-5 ${tn >= 0 ?"bg-emerald-50 border-emerald-300" :"bg-rose-50 border-rose-300"}`} data-testid="calc-ratio-tn">
 <p className={`text-[10px] font-bold uppercase mb-1 ${tn >= 0 ?"text-emerald-700" :"text-rose-700"}`}>
 Trésorerie nette (FRNG − BFR)
 </p>
 <p className={`font-mono font-extrabold text-3xl ${tn >= 0 ?"text-emerald-800" :"text-rose-800"}`}>
 {fmtEUR(tn)} €
 </p>
 <p className="text-xs text-slate-600 mt-2 leading-snug">
 {tn >= 0
 ?"Situation saine : les ressources stables financent le cycle d'exploitation et laissent un excédent en trésorerie."
 :"Tension de trésorerie : le BFR dépasse le FRNG, l'entreprise est dépendante des concours bancaires court terme."}
 </p>
 <p className="text-[11px] text-slate-500 mt-2">
 Trésorerie comptable réelle : <span className="font-mono font-bold">{fmtEUR(tnReel)} €</span>
 {Math.abs(tn - tnReel) > 1 && (
 <span className="italic text-rose-600 ml-2">⚠ écart de {fmtEUR(tn - tnReel)} €</span>
 )}
 </p>
 </div>
 </div>

 <div className="rounded-xl bg-[#15294F] text-white p-5">
 <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color:"#5AA0FF" }}>Lecture rapide</p>
 <p className="text-sm leading-relaxed">
 La règle d&apos;or : <strong>FRNG ≥ BFR</strong>. Si le FRNG est insuffisant, l&apos;entreprise finance son
 cycle d&apos;exploitation par découvert. Si le FRNG est très supérieur au BFR, l&apos;entreprise sur-immobilise ses ressources.
 </p>
 </div>
 </div>
 );
}

// ─── Conteneur avec onglets ──────────────────────────────────────────────
const TABS = [
 { id:"tva", lib:"Calcul de TVA", icon: Receipt, Comp: CalcTVA },
 { id:"amo", lib:"Amortissement", icon: Building2, Comp: CalcAmortissement },
 { id:"ratios", lib:"Ratios financiers", icon: BarChart3, Comp: CalcRatios },
];

export default function Calculateurs() {
 const [tab, setTab] = useState("tva");
 const Active = TABS.find((t) => t.id === tab)?.Comp || CalcTVA;

 return (
 <div data-testid="calculateurs-view" className="space-y-6">
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <Calculator className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Boîte à outils
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Calculateurs comptables
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 3 outils essentiels pour vos calculs quotidiens : conversion
 HT/TTC, plan d&apos;amortissement linéaire, et ratios financiers
 (FRNG, BFR, trésorerie nette).
 </p>
 </div>
 </div>

 <div className="mt-6 flex gap-1 p-1 bg-slate-100 rounded-xl">
 {TABS.map((t) => {
 const Icon = t.icon;
 const active = tab === t.id;
 return (
 <button
 key={t.id}
 data-testid={`calc-tab-${t.id}`}
 onClick={() => setTab(t.id)}
 className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition ${
 active ?"bg-white text-[#15294F] shadow-sm" :"text-slate-500 hover:text-[#15294F]"
 }`}
 >
 <Icon size={16} />
 <span>{t.lib}</span>
 </button>
 );
 })}
 </div>
 </div>

 <div className="bg-white border border-keyto rounded-2xl shadow-keyto-sm p-6 md:p-8">
 <Active />
 </div>
 </div>
 );
}
